import { isAxiosError } from "axios";
import { useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";

import { DelayedPlaceholder } from "../components/DelayedPlaceholder";
import { SegmentLoadError } from "../components/SegmentLoadError";
import { SegmentNotFound } from "../components/SegmentNotFound";
import { SEGMENT_MODE, type SegmentMode } from "../constants";
import { SegmentBuilderForm } from "../SegmentBuilderForm";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { Skeleton } from "@/components/ui/v2/skeleton";
import { useSegmentDetail } from "@/features/segments/api/useSegmentDetail";
import { useSegmentFields } from "@/features/segments/api/useSegmentFields";
import {
  getTourStepIds,
  SEGMENT_BUILDER_TOUR,
} from "@/features/segments/tour/segmentBuilderTour";
import { TourEntry } from "@/features/tours/components/TourEntry";
import { TourProvider } from "@/features/tours/TourProvider";
import { usePermissionMatrix } from "@/lib/hooks/usePermissionMatrix";

const HTTP_NOT_FOUND = 404;

type SegmentBuilderPageProps = {
  mode: SegmentMode;
};

function SegmentBuilderContent({ mode }: SegmentBuilderPageProps) {
  const { segmentId, clientId } = useParams();
  const {
    data: segment,
    isLoading,
    error,
    refetch,
  } = useSegmentDetail({ segmentId, clientId });
  const {
    data: fields,
    isPending: isFieldsPending,
    isError: isFieldsError,
    refetch: refetchFields,
  } = useSegmentFields();

  const isPageLoading = Boolean(segmentId && isLoading) || isFieldsPending;
  const replacesPlaceholder = useRef(false);
  useEffect(() => {
    replacesPlaceholder.current = isPageLoading;
  });

  if (isPageLoading) {
    return (
      <ModernPageLayout>
        <DelayedPlaceholder className="flex flex-col gap-4">
          <Skeleton className="h-9 w-72" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-48 w-full" />
        </DelayedPlaceholder>
      </ModernPageLayout>
    );
  }

  const isNotFound =
    isAxiosError(error) && error.response?.status === HTTP_NOT_FOUND;
  if (segmentId && !segment && error && !isNotFound)
    return <SegmentLoadError onRetry={() => refetch()} />;
  if (segmentId && (!segment || segment.archivedAt != null))
    return <SegmentNotFound />;
  if (isFieldsError)
    return <SegmentLoadError onRetry={() => refetchFields()} />;

  return (
    <>
      <SegmentBuilderForm
        key={`${segmentId ?? "new"}:${mode}`}
        mode={mode}
        segment={segment}
        segmentId={segmentId}
        clientId={clientId}
        fields={fields}
        fadeIn={!replacesPlaceholder.current}
      />
      <TourEntry visitKey={segmentId ?? "new"} />
    </>
  );
}

export function SegmentBuilderPage({ mode }: SegmentBuilderPageProps) {
  const { segmentId, clientId } = useParams();
  const { canEdit } = usePermissionMatrix();
  const { data: segment } = useSegmentDetail({ segmentId, clientId });
  const hasModeSwitch = Boolean(segmentId) && canEdit("segments");
  const hasSavedRules = Boolean(segment?.rules.length);
  const stepIds = useMemo(() => {
    const canEditSegment = mode === SEGMENT_MODE.EDIT || hasModeSwitch;
    return getTourStepIds({
      hasModeSwitch,
      hasHistory: Boolean(segmentId),
      canEdit: canEditSegment,
      hasArchive: canEditSegment && Boolean(segmentId),
      hasSavedRules,
    });
  }, [hasModeSwitch, segmentId, mode, hasSavedRules]);

  return (
    <TourProvider definition={SEGMENT_BUILDER_TOUR} stepIds={stepIds}>
      <SegmentBuilderContent mode={mode} />
    </TourProvider>
  );
}
